import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import ProductCard from "@/components/ProductCard";
import ServicesPromo from "@/components/ServicesPromo";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, Truck, BadgeCheck, SlidersHorizontal, X, Sparkles } from "lucide-react";

const HERO = "https://images.pexels.com/photos/21926656/pexels-photo-21926656.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONDITIONS = ["Nieuw", "Zo goed als nieuw", "Gebruikt", "Beschadigd"];

export default function Home() {
  const { user } = useAuth();
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [condition, setCondition] = useState("");
  const [sort, setSort] = useState("recent");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [promo, setPromo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    if (condition) params.condition = condition;
    if (sort) params.sort = sort;
    if (priceMin !== "") params.price_min = priceMin;
    if (priceMax !== "") params.price_max = priceMax;
    const { data } = await api.get("/products", { params });
    setProducts(data);
    setLoading(false);
  }, [q, category, condition, sort, priceMin, priceMax]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/settings").then(({ data }) => setPromo(!!data.free_promo)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) api.get("/favorites/ids").then(({ data }) => setFavIds(data)).catch(() => {});
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const toggleFav = async (id) => {
    const { data } = await api.post(`/favorites/${id}`);
    setFavIds((prev) => (data.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const clearFilters = () => { setCategory(""); setCondition(""); setQ(""); setPriceMin(""); setPriceMax(""); };

  return (
    <div>
      {promo && (
        <div data-testid="promo-banner" className="bg-gradient-to-r from-amber-500 to-rose-500 text-center py-2.5 px-4 text-sm font-medium text-amber-950 flex items-center justify-center gap-2">
          <Sparkles className="h-4 w-4" /> {t("home.promo")}
        </div>
      )}
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800/60">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-zinc-950/80 to-zinc-950" />
        <div className="absolute top-10 left-1/4 w-96 h-96 glow-rose blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-rose-800/40 bg-rose-950/30 px-4 py-1.5 mb-6 animate-fade-up">
            <span className="font-mono text-[11px] uppercase tracking-widest text-rose-300">{t("home.badge")}</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-100 max-w-3xl mx-auto animate-fade-up">
            {t("home.title1")} <span className="italic text-rose-400">{t("home.title2")}</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 max-w-xl mx-auto animate-fade-up">
            {t("home.subtitle")}
          </p>

          <div data-testid="hero-type-switch" className="mt-8 inline-flex rounded-full border border-zinc-800 bg-zinc-900/80 p-1 animate-fade-up">
            <span className="rounded-full bg-zinc-100 px-5 py-2 text-sm font-medium text-zinc-900">Producten</span>
            <Link to="/diensten" data-testid="hero-services-switch" className="flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-medium text-rose-300 hover:text-white hover:bg-rose-600 transition-colors">
              <Sparkles className="h-3.5 w-3.5" /> Diensten
            </Link>
          </div>

          <div className="mt-4 max-w-xl mx-auto animate-fade-up">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
              <input
                data-testid="product-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("home.search")}
                className="w-full rounded-full border border-zinc-800 bg-zinc-900/80 backdrop-blur pl-12 pr-4 py-4 text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none focus:ring-1 focus:ring-rose-700"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs text-zinc-500">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-rose-400" /> {t("home.trust_anon")}</span>
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-rose-400" /> {t("home.trust_ship")}</span>
            <span className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-rose-400" /> {t("home.trust_verified")}</span>
          </div>
        </div>
      </section>

      <ServicesPromo />

      {/* Catalog */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="category-all"
              onClick={() => setCategory("")}
              className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${category === "" ? "bg-rose-600 border-rose-600 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:text-zinc-100"}`}
            >
              {t("home.all")}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`category-${c}`}
                onClick={() => setCategory(c)}
                className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${category === c ? "bg-rose-600 border-rose-600 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:text-zinc-100"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <button
            data-testid="toggle-filters-button"
            onClick={() => setShowFilters((s) => !s)}
            className="shrink-0 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-400 hover:text-zinc-100"
          >
            <SlidersHorizontal className="h-4 w-4" /> {t("home.filters")}
          </button>
        </div>

        {showFilters && (
          <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 animate-fade-up">
            <select data-testid="filter-condition" value={condition} onChange={(e) => setCondition(e.target.value)} className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200">
              <option value="">{t("home.all_conditions")}</option>
              {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200">
              <option value="recent">{t("home.sort_recent")}</option>
              <option value="price_asc">{t("home.sort_price_asc")}</option>
              <option value="price_desc">{t("home.sort_price_desc")}</option>
            </select>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">€</span>
              <input
                data-testid="filter-price-min"
                type="number" min="0" inputMode="numeric"
                value={priceMin}
                onChange={(e) => setPriceMin(e.target.value)}
                placeholder="min"
                className="w-20 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 focus:border-rose-700 focus:outline-none"
              />
              <span className="text-xs text-zinc-500">–</span>
              <input
                data-testid="filter-price-max"
                type="number" min="0" inputMode="numeric"
                value={priceMax}
                onChange={(e) => setPriceMax(e.target.value)}
                placeholder="max"
                className="w-20 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 focus:border-rose-700 focus:outline-none"
              />
            </div>
            {(category || condition || q || priceMin !== "" || priceMax !== "") && (
              <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-rose-400 hover:text-rose-300">
                <X className="h-3.5 w-3.5" /> {t("home.clear")}
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-xl bg-zinc-900/60 animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-24">
            <p className="font-serif text-2xl text-zinc-300">{t("home.empty_title")}</p>
            <p className="text-sm text-zinc-500 mt-2">{t("home.empty_sub")}</p>
          </div>
        ) : (
          <div data-testid="product-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} favorited={favIds.includes(p.id)} onToggleFav={user ? toggleFav : null} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
